import React, { useMemo, useState } from 'react';
import {
  Box,
  Checkbox,
  Chip,
  Collapse,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography
} from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import PersonRemoveOutlinedIcon from '@mui/icons-material/PersonRemoveOutlined';
import { themeColors, useGlobalContext } from '@applications-terrains/birdz-react-library';
import { useTranslation } from 'react-i18next';
import { useInterventionCreation } from '../../context/interventionCreationContext';
import TablePagination from '../../../../../Tools/Table/TablePagination';
import { DEFAULT_PAGE_SIZE, clampPage, pageRows } from '../../../../../Tools/Table/pagination';
import { ACTION_NEEDED_CHIP_SX, selectedRowSx } from '../../theme';
import { PLACEHOLDER, SortDirection, displayValue, formatAddress } from '../Requests/requestRows';
import { useInterventionTypeLabel } from '../Requests/useInterventionTypeLabel';
import AssignmentDetail from './AssignmentDetail';
import {
  AssignmentRow,
  AssignmentSortColumn,
  appointmentSummary,
  hasTechnician,
  qualificationCaption,
  sortAssignments
} from './assignmentRows';
import { useRemoveAssignment } from './useRemoveAssignment';

type AssignmentTableProps = {
  rows: AssignmentRow[];
  isLoading: boolean;
  /**
   * Action de masse de l'étape, posée dans le pied collé de la table.
   *
   * Construite par `ToAssignPanel` et non ici : le renvoi partage son état avec l'envoi
   * du panneau technicien, et sa confirmation vit au même endroit. La table ne fait que
   * lui donner la place où elle reste atteignable pendant qu'on parcourt la liste.
   */
  action: React.ReactNode;
};

/** Cinq colonnes plus la case à cocher : voir la maquette de l'étape. */
const COLUMN_COUNT = 5;

export default function AssignmentTable({ rows, isLoading, action }: AssignmentTableProps) {
  const { t, i18n } = useTranslation();
  const { state, dispatch } = useInterventionCreation();
  const { removeAssignment, isRemoving } = useRemoveAssignment();
  const interventionTypeLabel = useInterventionTypeLabel();

  const [sortColumn, setSortColumn] = useState<AssignmentSortColumn>('servicePoint');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const sortedRows = useMemo(
    () => sortAssignments(rows, sortColumn, sortDirection),
    [rows, sortColumn, sortDirection]
  );

  // Dérivée plutôt que synchronisée par effet : la liste raccourcit après un envoi
  // ou une suppression, et la page courante peut alors ne plus exister.
  const currentPage = clampPage(page, rows.length, pageSize);
  const visibleRows = pageRows(sortedRows, currentPage, pageSize);

  const toggleSort = (column: AssignmentSortColumn) => {
    if (column === sortColumn) {
      setSortDirection((previous) => (previous === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortColumn(column);
    setSortDirection('asc');
  };

  const allSelected = rows.length > 0 && state.selectedRequestIds.length === rows.length;

  // Teinte de la ligne cochée : l'accent du thème choisi par l'utilisateur.
  const { theme } = useGlobalContext();
  const selectedSx = selectedRowSx(themeColors[theme]);

  const toggleAll = () =>
    dispatch({
      type: 'setSelectedRequests',
      ids: allSelected ? [] : rows.map((row) => row.id)
    });

  if (!isLoading && rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
        {t('interventionCreation.assignment.empty')}
      </Typography>
    );
  }

  return (
    <>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={allSelected}
                  indeterminate={state.selectedRequestIds.length > 0 && !allSelected}
                  onChange={toggleAll}
                  inputProps={{ 'aria-label': t('interventionCreation.requests.selectAll') }}
                />
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortColumn === 'servicePoint'}
                  direction={sortColumn === 'servicePoint' ? sortDirection : 'asc'}
                  onClick={() => toggleSort('servicePoint')}
                >
                  {t('interventionCreation.requests.columns.servicePoint')}
                </TableSortLabel>
              </TableCell>
              <TableCell>{t('interventionCreation.requests.columns.qualification')}</TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortColumn === 'technician'}
                  direction={sortColumn === 'technician' ? sortDirection : 'asc'}
                  onClick={() => toggleSort('technician')}
                >
                  {t('interventionCreation.assignment.columns.technician')}
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                {t('interventionCreation.requests.columns.actions')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((row) => {
              const isExpanded = expandedId === row.id;
              const isSelected = state.selectedRequestIds.includes(row.id);
              const isAssigned = hasTechnician(row);
              const caption = qualificationCaption(row, t);
              const appointment = appointmentSummary(row, t, i18n.language);

              return (
                <React.Fragment key={row.id}>
                  <TableRow hover sx={isSelected ? selectedSx : undefined}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isSelected}
                        onChange={() => dispatch({ type: 'toggleRequest', id: row.id })}
                        inputProps={{
                          'aria-label': t('interventionCreation.requests.selectRow', {
                            name: displayValue(row.service_point_customer_id)
                          })
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.5}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2" fontWeight={600}>
                            {displayValue(row.service_point_customer_id)}
                          </Typography>
                          {row.contract_data?.oss_code && (
                            <Chip size="small" label={row.contract_data.oss_code} />
                          )}
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          {formatAddress(row)}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.5} alignItems="flex-start">
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Typography variant="body2">{interventionTypeLabel(row)}</Typography>
                          {row.intervention_instructions !== null && (
                            <Tooltip title={row.intervention_instructions}>
                              <DescriptionOutlinedIcon
                                sx={{ fontSize: 16 }}
                                color="action"
                                aria-label={t(
                                  'interventionCreation.assignment.instructionsTooltip'
                                )}
                              />
                            </Tooltip>
                          )}
                          {appointment !== PLACEHOLDER && (
                            <Tooltip title={appointment}>
                              <EventOutlinedIcon
                                sx={{ fontSize: 16 }}
                                color="action"
                                aria-label={t('interventionCreation.assignment.appointmentTooltip')}
                              />
                            </Tooltip>
                          )}
                        </Stack>
                        {caption !== '' && (
                          <Typography variant="caption" color="text.secondary">
                            {caption}
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {isAssigned ? (
                        <Stack spacing={0.25}>
                          <Typography variant="body2">
                            {displayValue(row.technician_data?.name)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {displayValue(row.technician_data?.email)}
                          </Typography>
                        </Stack>
                      ) : (
                        <Chip
                          size="small"
                          sx={ACTION_NEEDED_CHIP_SX}
                          label={t('interventionCreation.assignment.toAssign')}
                        />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip
                        title={t(
                          isExpanded
                            ? 'interventionCreation.requests.collapseRow'
                            : 'interventionCreation.requests.expandRow'
                        )}
                      >
                        <IconButton
                          size="small"
                          aria-label={t(
                            isExpanded
                              ? 'interventionCreation.requests.collapseRow'
                              : 'interventionCreation.requests.expandRow'
                          )}
                          onClick={() => setExpandedId(isExpanded ? null : row.id)}
                        >
                          {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                      </Tooltip>
                      {/* `span` indispensable : désactivé — faute de technicien — le
                          bouton n'émet plus d'événement de souris, donc pas d'infobulle,
                          alors que c'est précisément le moment où elle est utile. */}
                      <Tooltip
                        title={t(
                          isAssigned
                            ? 'interventionCreation.assignment.unassignRow'
                            : 'interventionCreation.assignment.unassignUnavailable'
                        )}
                      >
                        <span>
                          <IconButton
                            size="small"
                            color="primary"
                            disabled={!isAssigned || isRemoving}
                            aria-label={t('interventionCreation.assignment.unassignRow')}
                            onClick={() => removeAssignment([row.id])}
                          >
                            <PersonRemoveOutlinedIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                  {/* Même teinte que la ligne principale : dépliée et cochée, l'ensemble
                      doit se lire comme un seul bloc. */}
                  <TableRow sx={isSelected ? selectedSx : undefined}>
                    <TableCell
                      colSpan={COLUMN_COUNT + 1}
                      sx={{ py: 0, border: isExpanded ? undefined : 'none' }}
                    >
                      <Collapse in={isExpanded} unmountOnExit>
                        <Box>
                          <AssignmentDetail row={row} />
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        total={rows.length}
        selectedCount={state.selectedRequestIds.length}
        page={currentPage}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        action={action}
      />
    </>
  );
}
